import React, { PureComponent, PropTypes } from 'react';
import shallowCompare from 'react-addons-shallow-compare';
import moment from 'moment';
import ReactDOM from 'react-dom';

import './MinuteBlock.css';

export default class MinuteBlock extends PureComponent {
    static propTypes = {
        time: PropTypes.object.isRequired,
        duration: PropTypes.number.isRequired,
        newStart: PropTypes.object,
        newEnd: PropTypes.object,
        roundedStart: PropTypes.object.isRequired,
        roundedEnd: PropTypes.object.isRequired,
        roundedMinStart: PropTypes.object.isRequired,
        roundedMaxEnd: PropTypes.object.isRequired,
        canEditStart: PropTypes.bool.isRequired,
        canEditEnd: PropTypes.bool.isRequired,
        editingStart: PropTypes.bool.isRequired,
        editingEnd: PropTypes.bool.isRequired,
        onTimeSelected: PropTypes.func.isRequired,
        onEditingStart: PropTypes.func.isRequired,
        onEditingEnd: PropTypes.func.isRequired,
        shouldScrollTo: PropTypes.bool,
        onScrollTo: PropTypes.func.isRequired,
        startTimeUnavailabilityReasons: PropTypes.array.isRequired,
        endTimeUnavailabilityReasons: PropTypes.array.isRequired,
        onUnavailableTimeSlotClicked: PropTypes.func.isRequired,
    }

    constructor(props) {
        super(props);

        this.state = this.calculateState(props);

        this.handleTimeClick = this.handleTimeClick.bind(this);
        this.handleStartClick = this.handleStartClick.bind(this);
        this.handleEndClick = this.handleEndClick.bind(this);
        this.handleUnavailableTimeInfoClick = this.handleUnavailableTimeInfoClick.bind(this);
    }

    componentDidMount() {
        if (this.props.shouldScrollTo)
            this.scrollTo();
    }

    componentWillReceiveProps(nextProps) {
        this.setState(this.calculateState(nextProps));
    }

    shouldComponentUpdate(nextProps, nextState) {
        // no props have changed we don't need to re-render
        if (!shallowCompare(this, nextProps, nextState))
            return false;

        // If the booking times change then the roundedStart/roundedEnd will have changed.
        // We are going to just re-render all minute blocks in the case as it is really hard to figure out
        // which blocks should re-render (dependant on the current editing state)
        if (this.props.roundedStart != nextProps.roundedStart || this.props.roundedEnd != nextProps.roundedEnd)
            return true;

        // TODO: support more than 1 prop changing

        if (this.props.shouldScrollTo !== nextProps.shouldScrollTo) {
            if (nextProps.shouldScrollTo)
                this.scrollTo();
            return true;
        }

        // TODO: Could improve this as we don't have to re-render the unavailable times

        const time = nextProps.time;
        const start = nextProps.newStart || nextProps.roundedStart;
        let end = nextProps.newEnd || nextProps.roundedEnd;
        // we want the start of the end minute block
        end = end.clone().add(-nextProps.duration, `m`);

        // Start or end editing changed check
        const startChangedAndShouldRender =
            (this.props.editingStart !== nextProps.editingStart) &&
            (time.isSameOrBefore(start) || (this.props.editingEnd && time.isSameOrAfter(end)));
        const endChangedAndShouldRender =
            (this.props.editingEnd !== nextProps.editingEnd) &&
            (time.isSameOrAfter(end) || (this.props.editingStart && time.isSameOrBefore(start)));

        if (startChangedAndShouldRender || endChangedAndShouldRender)
            return true;

        if (this.props.newStart != nextProps.newStart && nextProps.editingStart) {
            const oldStart = this.props.newStart || this.props.roundedStart;
            const startToCheck = start.isAfter(oldStart) ? start : oldStart;
            return time.isSameOrBefore(startToCheck);
        }

        if (this.props.newEnd != nextProps.newEnd && nextProps.editingEnd) {
            let oldEnd = this.props.newEnd || this.props.roundedEnd;
            oldEnd = oldEnd.clone().add(-nextProps.duration, `m`);
            const endToCheck = end.isBefore(oldEnd) ? end : oldEnd;
            return time.isSameOrAfter(endToCheck);
        }

        return false;
    }

    handleTimeClick(e) {
        if (!this.state.isUnavailable && (this.isStartEditTime() || this.isEndEditTime())) {
            this.props.onTimeSelected(this.props.time);
        } else if (this.state.isUnavailable ) {
            this.handleUnavailableTimeInfoClick();
        }

        e.stopPropagation();
    }

    handleStartClick(e) {
        if (this.props.canEditStart)
            this.props.onEditingStart();

        e.stopPropagation();
    }

    handleEndClick(e) {
        if (this.props.canEditEnd)
            this.props.onEditingEnd();

        e.stopPropagation();
    }

    handleUnavailableTimeInfoClick() {
        const reasons = this.state.isUnavailableBeforeMinStart ?
            this.props.startTimeUnavailabilityReasons : this.props.endTimeUnavailabilityReasons;

        this.props.onUnavailableTimeSlotClicked(reasons);
    }

    calculateState(props) {
        const {
            time,
            duration,
            newStart,
            newEnd,
            roundedMinStart,
            roundedMaxEnd,
            roundedStart,
            roundedEnd } = props;

        const start = newStart || roundedStart;
        const end = newEnd || roundedEnd;
        const isUnavailable = this.checkTimeIsUnavailable(time, roundedMinStart, roundedMaxEnd);

        return {
            isUnavailable,
            isPreviousUnavailable: this.checkTimeIsUnavailable(moment(time).subtract(duration, `m`), roundedMinStart, roundedMaxEnd),
            isUnavailableBeforeMinStart: time.isBefore(roundedMinStart),
            isUnavailableAfterMaxEnd: time.isSameOrAfter(roundedMaxEnd),
            isBookingTime: time.unix() >= start.unix() && time.unix() < end.unix(),
            isStartBookingBlock: time.isSame(start),
            isEndBookingBlock: time.isSame(moment(end).subtract(duration, `m`)),
        };
    }

    scrollTo() {
        const element = ReactDOM.findDOMNode(this);
        const elementRect = element.getBoundingClientRect();

        this.props.onScrollTo(elementRect.top);
    }

    isStartEditTime() {
        const end = this.props.newEnd || this.props.roundedEnd;
        return this.props.editingStart && this.props.time.unix() < end.unix();
    }

    isEndEditTime() {
        const start = this.props.newStart || this.props.roundedStart;
        return this.props.editingEnd && this.props.time.unix() >= start.unix();
    }

    checkTimeIsUnavailable(time, min, max) {
        return time.unix() < min.unix() || time.unix() >= max.unix();
    }

    getEditHandlesJsx() {
        const startDisabledCssClass = this.props.canEditStart ? `` : `disabled`;
        const startEditingCssClass = this.props.editingStart ? `editing` : ``;
        const endDisabledCssClass = this.props.canEditEnd ? `` : `disabled`;
        const endEditingCssClass = this.props.editingEnd ? `editing` : ``;

        const startHandleJsx = this.state.isStartBookingBlock ?
            <div className={`handle start-handle ${startDisabledCssClass} ${startEditingCssClass}`} onClick={this.handleStartClick}>
                <i className="material-icons">&#xE3C9;</i>
            </div> : null;

        const endHandleJsx = this.state.isEndBookingBlock ?
            <div className={`handle end-handle ${endDisabledCssClass} ${endEditingCssClass}`} onClick={this.handleEndClick}>
                <i className="material-icons">&#xE3C9;</i>
            </div> : null;

        return {
            startHandleJsx,
            endHandleJsx
        };
    }

    render() {
        const unavailableClassName = this.state.isUnavailable ? `unavailable-time` : ``;
        const prevUnavailableClassName = this.state.isPreviousUnavailable ? `prev-unavailable-time` : ``;
        const bookingClassName = this.state.isBookingTime ? `booking-time` : ``;
        const bookingStartCssClass = this.state.isStartBookingBlock ? `booking-start-block` : ``;
        const bookingEndCssClass = this.state.isEndBookingBlock ? `booking-end-block` : ``;
        const isEditingEnabled = this.isStartEditTime() || this.isEndEditTime();
        const editModeEnabledCssClass = isEditingEnabled ? `editing-enabled` : ``;
        const cssClasses = `minute-block ${unavailableClassName} ${prevUnavailableClassName} ${bookingClassName} ${bookingStartCssClass} ${bookingEndCssClass} ${editModeEnabledCssClass}`;

        const { startHandleJsx, endHandleJsx } = this.getEditHandlesJsx();

        const clickableIndicatorJsx = isEditingEnabled ?
            <div className="clickable-indicator"></div> : null;

        return (
            <div className={cssClasses} onClick={this.handleTimeClick}>
                {clickableIndicatorJsx}
                {startHandleJsx}
                {endHandleJsx}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./scripts/components/Common/TimeSelector/MinuteBlock.jsx